import { type BrandName } from "@elit-hair/brands";
import { useQuery } from "@tanstack/react-query";

import { callSvcAxiosInstance } from "@/constants";
import { callServiceEndpoints } from "@/constants/endpoints";
import { getContactById, getLeadById } from "@/hooks/queries/call.queries";
import type { ModuleType } from "@/types/zoho.types";

type CrmPersonByEmailResponse = {
    lead: { id: string } | null;
    contact: { id: string } | null;
};

const getCrmPersonByEmail = async (email: string): Promise<CrmPersonByEmailResponse> => {
    const response = await callSvcAxiosInstance.get<CrmPersonByEmailResponse>(
        callServiceEndpoints.getPersonByEmail(email)
    );
    return response.data;
};

export const useCrmLeadByEmail = (email: string | null | undefined, brandName?: BrandName | null) => {
    const trimmedEmail = email?.trim();

    return useQuery({
        queryKey: ["crm-lead-by-email", trimmedEmail, brandName],
        queryFn: () => getCrmPersonByEmail(trimmedEmail!),
        enabled: Boolean(trimmedEmail),
        staleTime: 60_000,
        retry: false,
    });
};

export const useCrmLeadVerification = (
    zohoLeadId: string | null | undefined,
    email: string | null | undefined,
    brandName?: BrandName | null
) => {
    const trimmedZohoLeadId = zohoLeadId?.trim();
    const trimmedEmail = email?.trim().toLowerCase();

    return useQuery({
        queryKey: ["crm-lead-verification", trimmedZohoLeadId, trimmedEmail, brandName],
        queryFn: () => getLeadById(trimmedZohoLeadId!, brandName!),
        enabled: Boolean(trimmedZohoLeadId && brandName),
        staleTime: 60_000,
        retry: false,
    });
};

const RECORD_SERVICES = {
    Leads: (id: string, brand: BrandName) => getLeadById(id, brand),
    Contacts: (id: string, brand: BrandName) => getContactById(id, brand),
    // Future: Add support for other module types
} as const;

type SupportedModuleType = keyof typeof RECORD_SERVICES;

function isSupportedModuleType(value: ModuleType): value is SupportedModuleType {
    return value === "Leads" || value === "Contacts";
}

export const useRecord = (recordId: string, moduleType: ModuleType, brandName?: BrandName | null) => {
    return useQuery({
        enabled: !!recordId && !!brandName,
        queryKey: ["record", recordId, moduleType, brandName],
        queryFn: async () => {
            if (!isSupportedModuleType(moduleType)) {
                throw new Error(`Module type ${moduleType} not yet supported`);
            }
            return RECORD_SERVICES[moduleType](recordId, brandName!);
        },
    });
};
